/**
 * « Vous travaillez pour <industriel> » (PT-119).
 *
 * Inside an industriel a surgeon reads as a manufacturer: their patients, the laboratory and the
 * télé-expertise are gone. Without this line the same absence would read as a fault, not as the
 * context they chose — and the way back would be three screens away.
 */
import { useState } from "react";
import { Building2 } from "lucide-react";
import { Trans } from "@lingui/macro";
import { isOrganisationsResponse } from "@app/components/organisation/types";
import API from "@app/services/api";
import { workIn } from "@app/utils/organisation-context";
import useUser from "@app/zustand/user";

interface ContextUser {
  account_role?: string;
  organisation?: { id: string; raison_sociale: string; type: string } | null;
}

export default function OrganisationContextBanner() {
  const user = useUser((state) => state.user) as ContextUser | null;
  const [leaving, setLeaving] = useState(false);
  const organisation = user?.organisation;
  const accountRole = (user?.account_role ?? "").toLowerCase();

  // A manufacturer's own reader is in its natural context, and an admin is never narrowed.
  if (
    !organisation ||
    organisation.type !== "industriel" ||
    accountRole === "externe" ||
    accountRole === "admin"
  ) {
    return null;
  }

  return (
    <div className="flex items-center justify-between gap-3 bg-blue-50 border-b border-primary/20 px-4 py-2 text-sm text-blue-900">
      <span className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-primary shrink-0" />
        <Trans>
          Vous travaillez pour <strong>{organisation.raison_sociale}</strong> :
          catalogue et données agrégées uniquement, vos patients ne sont pas
          visibles.
        </Trans>
      </span>
      <button
        type="button"
        disabled={leaving}
        onClick={() => {
          setLeaving(true);
          // Back to a care organisation when the account has one, else to no organisation at all.
          API.get({ path: "/organisation" })
            .then((res: unknown) => {
              const care = isOrganisationsResponse(res)
                ? res.organisations.find((o) => !o.narrows_session)
                : undefined;
              return workIn(care?.id ?? null);
            })
            .finally(() => setLeaving(false));
        }}
        className="font-medium text-primary hover:text-primary/80 whitespace-nowrap disabled:opacity-50"
      >
        <Trans>Revenir à mon activité de soins</Trans>
      </button>
    </div>
  );
}
